import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Upload, X, GripVertical, Eye, EyeOff, ExternalLink } from "lucide-react";
import StaffLayout from "@/components/staff/StaffLayout";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

function abs(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${API}${url.startsWith("/api") ? url.slice(4) : url}`;
}

export default function StaffPartnersPage() {
  const [partners, setPartners] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  function load() {
    api.get("/partners?enabled_only=false").then((r) => setPartners(r.data));
  }
  useEffect(load, []);

  async function toggle(p) {
    await api.put(`/admin/partners/${p.id}`, { ...p, enabled: !p.enabled });
    load();
  }

  async function remove(p) {
    if (!confirm(`Delete partner "${p.name}"?`)) return;
    await api.delete(`/admin/partners/${p.id}`);
    toast.success("Deleted");
    load();
  }

  async function reorder(p, dir) {
    const sorted = [...partners].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((x) => x.id === p.id);
    const j = idx + dir;
    if (j < 0 || j >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[j];
    await Promise.all([
      api.put(`/admin/partners/${a.id}`, { ...a, order: b.order }),
      api.put(`/admin/partners/${b.id}`, { ...b, order: a.order }),
    ]);
    load();
  }

  return (
    <StaffLayout>
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl">Partners</h1>
          <p className="text-sm text-[#71717A] mt-1">OTAs, distribution partners, awards — shown in the public footer with their logo + a link.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="partner-add" className="bg-brand-primary text-white px-5 py-2.5 rounded-sm text-sm flex items-center gap-2">
          <Plus size={14} /> Add partner
        </button>
      </div>

      <div className="mt-8 bg-white border border-[#E4E4E7] rounded-md divide-y">
        {partners.sort((a, b) => a.order - b.order).map((p) => (
          <div key={p.id} data-testid={`partner-row-${p.id}`} className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 flex-wrap sm:flex-nowrap">
            <GripVertical size={14} className="text-[#A1A1AA] hidden sm:block" />
            <div className="w-20 h-10 sm:w-24 sm:h-12 flex items-center justify-center bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm overflow-hidden shrink-0">
              {p.logo_url ? <img src={abs(p.logo_url)} alt={p.name} className="max-w-full max-h-full" /> : <span className="text-[10px] text-[#A1A1AA]">no logo</span>}
            </div>
            <div className="flex-1 min-w-0 basis-full sm:basis-auto order-3 sm:order-none">
              <div className="font-medium truncate">{p.name}</div>
              {p.url && <a href={p.url} target="_blank" rel="noreferrer" className="text-xs text-brand-muted hover:text-brand-primary inline-flex items-center gap-1 max-w-full">
                <span className="truncate">{p.url}</span> <ExternalLink size={10} className="shrink-0" />
              </a>}
            </div>
            <div className="flex items-center gap-1 sm:gap-2 ml-auto sm:ml-0">
              <button onClick={() => reorder(p, -1)} className="text-xs px-2 hover:text-brand-text">↑</button>
              <button onClick={() => reorder(p, +1)} className="text-xs px-2 hover:text-brand-text">↓</button>
              <button data-testid={`partner-toggle-${p.id}`} onClick={() => toggle(p)} className={`text-xs px-2 sm:px-3 py-1.5 rounded-sm whitespace-nowrap ${p.enabled ? "bg-emerald-50 text-emerald-700" : "bg-[#FAFAFA] text-[#71717A]"}`}>
                {p.enabled ? <Eye size={12} className="inline mr-1" /> : <EyeOff size={12} className="inline mr-1" />}
                <span className="hidden sm:inline">{p.enabled ? "Visible" : "Hidden"}</span>
              </button>
              <button data-testid={`partner-edit-${p.id}`} onClick={() => setEditing(p)} className="text-xs text-brand-primary hover:underline">Edit</button>
              <button data-testid={`partner-del-${p.id}`} onClick={() => remove(p)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-sm"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {partners.length === 0 && <div className="p-8 text-center text-sm text-[#71717A]">No partners yet — click "Add partner".</div>}
      </div>

      {showAdd && <PartnerModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editing && <PartnerModal partner={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </StaffLayout>
  );
}

function PartnerModal({ partner, onClose, onSaved }) {
  const isEdit = !!partner;
  const [f, setF] = useState(partner ? { ...partner } : { name: "", url: "", logo_url: "", enabled: true, order: 99 });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  async function uploadLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/upload/partner-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF({ ...f, logo_url: r.data.url });
      toast.success("Logo uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  }

  async function save(e) {
    e.preventDefault();
    try {
      if (isEdit) {
        await api.put(`/admin/partners/${partner.id}`, f);
        toast.success("Updated");
      } else {
        await api.post("/admin/partners", f);
        toast.success("Created");
      }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={save} className="bg-white w-full max-w-lg rounded-md">
        <div className="p-5 border-b flex justify-between items-center">
          <h2 className="font-display text-2xl">{isEdit ? "Edit partner" : "New partner"}</h2>
          <button type="button" onClick={onClose} className="p-2"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#71717A]">Name</span>
            <input data-testid="partner-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#71717A]">URL</span>
            <input data-testid="partner-url" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://..." className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
          </label>
          <div>
            <span className="text-xs uppercase tracking-wider text-[#71717A]">Logo</span>
            <div className="mt-1 flex items-center gap-4">
              <div className="w-28 h-14 border border-[#E4E4E7] bg-[#FAFAFA] flex items-center justify-center overflow-hidden rounded-sm">
                {f.logo_url ? <img src={abs(f.logo_url)} alt="logo" className="max-w-full max-h-full" /> : <span className="text-[10px] text-[#A1A1AA]">no logo</span>}
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={uploadLogo} className="hidden" data-testid="partner-file" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 bg-brand-primary text-white px-4 py-2 rounded-sm text-xs">
                <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
              </button>
            </div>
            <label className="block mt-3">
              <span className="text-[10px] uppercase tracking-wider text-[#71717A]">…or paste URL</span>
              <input value={f.logo_url} onChange={(e) => setF({ ...f, logo_url: e.target.value })} placeholder="https://..." className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#71717A]">Display order</span>
            <input type="number" data-testid="partner-order" value={f.order} onChange={(e) => setF({ ...f, order: parseInt(e.target.value || 0, 10) })} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} />
            <span className="text-sm">Show on public site</span>
          </label>
        </div>
        <div className="p-5 border-t flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm">Cancel</button>
          <button data-testid="partner-save" type="submit" className="bg-brand-primary text-white px-6 py-2.5 rounded-sm text-sm">{isEdit ? "Save" : "Create"}</button>
        </div>
      </form>
    </div>
  );
}
